import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import leavingService from "../../services/leavingService";
import studentService from "../../services/studentService";

const todayDMY = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(
    d.getMonth() + 1,
  ).padStart(2, "0")}/${d.getFullYear()}`;
};

const EMPTY_FORM = {
  student: "",
  serialNo: "",
  lastSchoolAttended: "",
  progress: "Good",
  conduct: "Good",
  dateOfAdmission: "",
  admissionSerialNo: "",
  standard: "",
  since: "",
  dateOfLeaving: "",
  reasonOfLeaving: "Completion of Course",
  remark: "",
  dateOfIssue: todayDMY(),
};

const asTrimmedString = (value) => String(value ?? "").trim();

const buildStandard = (student) =>
  [student?.courseAdmitted, student?.courseYear].filter(Boolean).join(" ");

const dmyToYmd = (value) => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(asTrimmedString(value));
  if (!match) return "";
  const [, dd, mm, yyyy] = match;
  return `${yyyy}-${mm}-${dd}`;
};

const ymdToDmy = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(asTrimmedString(value));
  if (!match) return "";
  const [, yyyy, mm, dd] = match;
  return `${dd}/${mm}/${yyyy}`;
};

const clearStudentLinkedFields = (prev) => ({
  ...prev,
  student: "",
  lastSchoolAttended: "",
  standard: "",
  dateOfAdmission: "",
});

export default function LeavingForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { academicYear, user, isClerk } = useApp();
  const isEdit = Boolean(id);
  const listPath = isClerk ? "/clerk/leaving" : "/leaving";

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [students, setStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await leavingService.getById(id);
        const c = res.data;
        setForm({
          student: c.student._id,
          serialNo: String(c.serialNo),
          lastSchoolAttended: c.lastSchoolAttended,
          progress: c.progress,
          conduct: c.conduct,
          dateOfAdmission: c.dateOfAdmission,
          admissionSerialNo: c.admissionSerialNo,
          standard: c.standard,
          since: c.since,
          dateOfLeaving: c.dateOfLeaving,
          reasonOfLeaving: c.reasonOfLeaving,
          remark: c.remark || "",
          dateOfIssue: c.dateOfIssue,
        });
        setSelectedStudent(c.student);
        setStudentSearch(c.student.fullName);
      } catch (err) {
        toast.error(err.message);
        navigate(listPath);
      } finally {
        setFetching(false);
      }
    })();
  }, [id, isEdit, navigate, listPath]);

  useEffect(() => {
    if (isEdit) return;
    const t = setTimeout(async () => {
      if (!asTrimmedString(studentSearch)) {
        setStudents([]);
        return;
      }
      setLoadingStudents(true);
      try {
        const res = await studentService.getAll({
          search: studentSearch,
          limit: 8,
          academicYear: academicYear?._id || "",
        });
        setStudents(res.data);
      } catch {
        setStudents([]);
      } finally {
        setLoadingStudents(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [studentSearch, isEdit, academicYear]);

  const selectStudent = (student) => {
    setSelectedStudent(student);
    setStudents([]);
    setStudentSearch(student.fullName);
    setForm((prev) => ({
      ...prev,
      student: student._id,
      lastSchoolAttended: student.previousSchoolName || "",
      standard: buildStandard(student),
      dateOfAdmission: student.instituteAdmissionDate || "",
    }));
    setErrors((prev) => ({
      ...prev,
      student: "",
      lastSchoolAttended: "",
      standard: "",
      dateOfAdmission: "",
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleStudentSearchChange = (e) => {
    const value = e.target.value;
    setStudentSearch(value);

    if (selectedStudent) {
      setSelectedStudent(null);
      setForm((prev) => clearStudentLinkedFields(prev));
    }

    if (errors.student) {
      setErrors((prev) => ({ ...prev, student: "" }));
    }
  };

  const handleLeavingDateChange = (e) => {
    const value = ymdToDmy(e.target.value);
    setForm((prev) => ({ ...prev, dateOfLeaving: value }));
    if (errors.dateOfLeaving) {
      setErrors((prev) => ({ ...prev, dateOfLeaving: "" }));
    }
  };

  const handleIssueDateChange = (e) => {
    const value = ymdToDmy(e.target.value);
    setForm((prev) => ({ ...prev, dateOfIssue: value }));
    if (errors.dateOfIssue) {
      setErrors((prev) => ({ ...prev, dateOfIssue: "" }));
    }
  };

  const validate = () => {
    const errs = {};
    const dateRx = /^\d{2}\/\d{2}\/\d{4}$/;

    if (!form.student) errs.student = "Select a student";
    if (!asTrimmedString(form.serialNo)) errs.serialNo = "Required";
    if (isNaN(Number(form.serialNo))) errs.serialNo = "Must be a number";
    if (!asTrimmedString(form.lastSchoolAttended)) errs.lastSchoolAttended = "Required";
    // if (!dateRx.test(form.dateOfAdmission)) errs.dateOfAdmission = "Format: DD/MM/YYYY";
    if (!asTrimmedString(form.admissionSerialNo)) errs.admissionSerialNo = "Required";
    if (!asTrimmedString(form.standard)) errs.standard = "Required";
    if (!/^\d{4}$/.test(form.since)) errs.since = "4-digit year";
    if (!dateRx.test(form.dateOfLeaving)) errs.dateOfLeaving = "Select a valid date";
    if (!asTrimmedString(form.reasonOfLeaving)) errs.reasonOfLeaving = "Required";
    if (!dateRx.test(form.dateOfIssue)) errs.dateOfIssue = "Select a valid date";

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    if (!isEdit && !academicYear?._id) {
      toast.error("Select an Academic Year from the Dashboard first.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...form,
        serialNo: Number(form.serialNo),
        ...(!isEdit && { academicYear: academicYear._id }),
        ...(selectedStudent?.college && {
          college: selectedStudent.college?._id || selectedStudent.college,
        }),
      };

      if (isEdit) {
        await leavingService.update(id, payload);
        toast.success("Certificate updated");
      } else {
        await leavingService.create(payload);
        toast.success("Certificate issued");
      }
      navigate(listPath);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="loading-center">
        <div className="spinner" />
        Loading...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 860 }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            {isEdit ? "Edit Leaving Certificate" : "Issue Leaving Certificate (TC)"}
          </h1>
          <p className="page-subtitle">
            {academicYear ? `Academic Year: ${academicYear.label}` : ""}
          </p>
        </div>
        <button className="btn btn-outline" onClick={() => navigate(listPath)}>
          Back
        </button>
      </div>

      {!isEdit && !academicYear && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: 8,
            padding: "0.75rem 1rem",
            marginBottom: "1rem",
            fontSize: 13,
            color: "#dc2626",
          }}
        >
          No academic year selected. Go to Dashboard first.
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="card" style={{ marginBottom: "1rem" }}>
          <div className="card-header">Student</div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">
                Select Student <span style={{ color: "var(--danger)" }}>*</span>
              </label>
              {!isEdit && (
                <div className="student-select-box">
                  <div className="student-select-search">
                    <svg
                      width="14"
                      height="14"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      style={{ color: "#9ca3af" }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                    <input
                      placeholder="Type student name or enrollment no..."
                      value={studentSearch}
                      onChange={handleStudentSearchChange}
                    />
                    {loadingStudents && (
                      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                        ...
                      </span>
                    )}
                  </div>
                  {students.length > 0 && (
                    <div className="student-select-list">
                      {students.map((student) => (
                        <div
                          key={student._id}
                          className={`student-select-item ${selectedStudent?._id === student._id ? "selected" : ""
                            }`}
                          onClick={() => selectStudent(student)}
                        >
                          <div className="student-name">{student.fullName}</div>
                          <div className="student-meta">
                            {student.enrollmentNo} � {buildStandard(student)} � DOB: {student.dob}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {selectedStudent && (
                <div
                  style={{
                    marginTop: "0.5rem",
                    padding: "0.65rem 0.85rem",
                    background: "#e8f1f8",
                    border: "1px solid #b6d5ec",
                    borderRadius: "var(--radius)",
                    fontSize: 13,
                  }}
                >
                  <strong>{selectedStudent.fullName}</strong>
                  <span
                    style={{ color: "var(--text-muted)", marginLeft: "0.75rem" }}
                  >
                    {selectedStudent.enrollmentNo} � Aadhaar: {selectedStudent.aadharNo}
                  </span>
                </div>
              )}
              {errors.student && <span className="form-error">{errors.student}</span>}
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: "1rem" }}>
          <div className="card-header">Certificate Details</div>
          <div className="card-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  Serial No. <span style={{ color: "var(--danger)" }}>*</span>
                </label>
                <input
                  type="number"
                  name="serialNo"
                  value={form.serialNo}
                  onChange={handleChange}
                  min={1}
                  placeholder="From pre-printed form"
                  className={`form-control ${errors.serialNo ? "error" : ""}`}
                />
                {errors.serialNo && <span className="form-error">{errors.serialNo}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Date of Issue <span style={{ color: "var(--danger)" }}>*</span>
                </label>
                <input
                  type="date"
                  name="dateOfIssue"
                  value={dmyToYmd(form.dateOfIssue)}
                  onChange={handleIssueDateChange}
                  className={`form-control ${errors.dateOfIssue ? "error" : ""}`}
                />
                {errors.dateOfIssue && <span className="form-error">{errors.dateOfIssue}</span>}
              </div>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: "1rem" }}>
          <div className="card-header">Academic Details</div>
          <div className="card-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  Last School / College Attended <span style={{ color: "var(--danger)" }}>*</span>
                </label>
                <input
                  name="lastSchoolAttended"
                  value={form.lastSchoolAttended}
                  onChange={handleChange}
                  placeholder="e.g. Rahbar Jr. College, Akola"
                  className={`form-control ${errors.lastSchoolAttended ? "error" : ""}`}
                />
                {errors.lastSchoolAttended && (
                  <span className="form-error">{errors.lastSchoolAttended}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Standard / Course <span style={{ color: "var(--danger)" }}>*</span>
                </label>
                <input
                  name="standard"
                  value={form.standard}
                  onChange={handleChange}
                  placeholder="e.g. B.Pharm Final Year"
                  className={`form-control ${errors.standard ? "error" : ""}`}
                />
                {errors.standard && <span className="form-error">{errors.standard}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Since (Year) <span style={{ color: "var(--danger)" }}>*</span>
                </label>
                <input
                  name="since"
                  value={form.since}
                  onChange={handleChange}
                  placeholder="e.g. 2021"
                  className={`form-control ${errors.since ? "error" : ""}`}
                />
                <span className="form-hint">4-digit year</span>
                {errors.since && <span className="form-error">{errors.since}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Date of Admission <span style={{ color: "var(--danger)" }}>*</span>
                </label>
                <input
                  name="dateOfAdmission"
                  value={form.dateOfAdmission}
                  onChange={handleChange}
                  placeholder="DD/MM/YYYY"
                  className={`form-control ${errors.dateOfAdmission ? "error" : ""}`}
                />
                {errors.dateOfAdmission && (
                  <span className="form-error">{errors.dateOfAdmission}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Admission Serial No. <span style={{ color: "var(--danger)" }}>*</span>
                </label>
                <input
                  name="admissionSerialNo"
                  value={form.admissionSerialNo}
                  onChange={handleChange}
                  placeholder="e.g. 146"
                  className={`form-control ${errors.admissionSerialNo ? "error" : ""}`}
                />
                {errors.admissionSerialNo && (
                  <span className="form-error">{errors.admissionSerialNo}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Progress</label>
                <select
                  name="progress"
                  value={form.progress}
                  onChange={handleChange}
                  className="form-control"
                >
                  {["Excellent", "Very Good", "Good", "Average", "Poor"].map((option) => (
                    <option key={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Conduct</label>
                <select
                  name="conduct"
                  value={form.conduct}
                  onChange={handleChange}
                  className="form-control"
                >
                  {["Excellent", "Very Good", "Good", "Average", "Poor"].map((option) => (
                    <option key={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: "1.5rem" }}>
          <div className="card-header">Leaving Details</div>
          <div className="card-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  Date of Leaving <span style={{ color: "var(--danger)" }}>*</span>
                </label>
                <input
                  type="date"
                  value={dmyToYmd(form.dateOfLeaving)}
                  onChange={handleLeavingDateChange}
                  className={`form-control ${errors.dateOfLeaving ? "error" : ""}`}
                />
                {errors.dateOfLeaving && (
                  <span className="form-error">{errors.dateOfLeaving}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Reason of Leaving <span style={{ color: "var(--danger)" }}>*</span>
                </label>
                <input
                  name="reasonOfLeaving"
                  value={form.reasonOfLeaving}
                  onChange={handleChange}
                  placeholder="e.g. Completion of Course"
                  className={`form-control ${errors.reasonOfLeaving ? "error" : ""}`}
                />
                {errors.reasonOfLeaving && (
                  <span className="form-error">{errors.reasonOfLeaving}</span>
                )}
              </div>
            </div>

            <div className="form-group" style={{ marginTop: "0.75rem" }}>
              <label className="form-label">Remark</label>
              <textarea
                name="remark"
                value={form.remark}
                onChange={handleChange}
                rows={2}
                placeholder="e.g. He has passed B.Pharm Final Year Exam held in Summer-2025"
                className="form-control"
                style={{ resize: "vertical" }}
              />
              <span className="form-hint">
                Long remarks auto-split across two lines on the certificate
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-outline" onClick={() => navigate("/leaving")}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Saving..." : isEdit ? "Update Certificate" : "Issue Certificate"}
          </button>
        </div>
      </form>
    </div>
  );
}
